import { useState, useEffect } from "react";
import { Building2, Plus, Search, Phone, Mail, MapPin, Edit2, Trash2, Eye, IndianRupee, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const API = process.env.REACT_APP_BACKEND_URL;

export default function CustomerManagement() {
  const { token, user } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerDetail, setCustomerDetail] = useState(null);
  const [form, setForm] = useState({
    company_name: "", contact_name: "", address: "", phone: "", email: "", gst_number: "", notes: ""
  });

  const canEdit = ["owner", "manager"].includes(user?.role);

  useEffect(() => { fetchCustomers(); }, []);

  const fetchCustomers = async () => {
    try {
      const res = await fetch(`${API}/api/customers`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) setCustomers(await res.json());
    } catch (e) { toast.error("Failed to load customers"); }
    finally { setLoading(false); }
  };

  const fetchCustomerDetail = async (customerId) => {
    try {
      const res = await fetch(`${API}/api/customers/${customerId}/balance`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) {
        setCustomerDetail(await res.json());
        setShowDetailModal(true);
      }
    } catch (e) { toast.error("Failed to load customer details"); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const method = selectedCustomer ? "PUT" : "POST";
    const url = selectedCustomer ? `${API}/api/customers/${selectedCustomer.id}` : `${API}/api/customers`;
    try {
      const res = await fetch(url, {
        method, headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(form)
      });
      if (res.ok) {
        toast.success(selectedCustomer ? "Customer updated" : "Customer added");
        setShowModal(false);
        resetForm();
        fetchCustomers();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to save customer");
      }
    } catch (e) { toast.error("Failed to save customer"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this customer?")) return;
    try {
      const res = await fetch(`${API}/api/customers/${id}`, {
        method: "DELETE", headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) { toast.success("Customer deleted"); fetchCustomers(); }
      else {
        const err = await res.json();
        toast.error(err.detail || "Failed to delete");
      }
    } catch (e) { toast.error("Failed to delete customer"); }
  };

  const openEdit = (customer) => {
    setSelectedCustomer(customer);
    setForm({ ...customer });
    setShowModal(true);
  };

  const resetForm = () => {
    setSelectedCustomer(null);
    setForm({ company_name: "", contact_name: "", address: "", phone: "", email: "", gst_number: "", notes: "" });
  };

  const filtered = customers.filter(c =>
    c.company_name.toLowerCase().includes(search.toLowerCase()) ||
    c.contact_name.toLowerCase().includes(search.toLowerCase()) ||
    c.gst_number?.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) return <div className="flex justify-center py-12"><div className="w-8 h-8 border-4 border-forest/20 border-t-forest rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-6" data-testid="customer-management">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-serif font-bold text-navy">Customers</h1>
          <p className="text-slate-500 text-sm font-sans">Manage your business customers</p>
        </div>
        <Button onClick={() => { resetForm(); setShowModal(true); }} className="bg-forest hover:bg-forest/90" data-testid="add-customer-btn">
          <Plus className="w-4 h-4 mr-2" /> Add Customer
        </Button>
      </div>

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input placeholder="Search by name, company or GST..." value={search} onChange={e => setSearch(e.target.value)} className="pl-10" data-testid="search-customers" />
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {filtered.map(customer => (
          <Card key={customer.id} className="hover:shadow-md transition-shadow" data-testid={`customer-card-${customer.id}`}>
            <CardContent className="p-4">
              <div className="flex justify-between items-start mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-forest/10 rounded-lg flex items-center justify-center">
                    <Building2 className="w-5 h-5 text-forest" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-navy font-sans">{customer.company_name}</h3>
                    <p className="text-sm text-slate-500">{customer.contact_name}</p>
                  </div>
                </div>
              </div>

              <div className="space-y-2 text-sm text-slate-600 mb-4">
                {customer.phone && <div className="flex items-center gap-2"><Phone className="w-3.5 h-3.5" />{customer.phone}</div>}
                {customer.email && <div className="flex items-center gap-2"><Mail className="w-3.5 h-3.5" />{customer.email}</div>}
                {customer.gst_number && <Badge variant="outline" className="text-xs">{customer.gst_number}</Badge>}
              </div>

              <div className="flex gap-2 pt-3 border-t">
                <Button size="sm" variant="outline" onClick={() => fetchCustomerDetail(customer.id)} data-testid={`view-customer-${customer.id}`}>
                  <Eye className="w-3.5 h-3.5 mr-1" /> View
                </Button>
                {canEdit && (
                  <>
                    <Button size="sm" variant="outline" onClick={() => openEdit(customer)} data-testid={`edit-customer-${customer.id}`}>
                      <Edit2 className="w-3.5 h-3.5" />
                    </Button>
                    <Button size="sm" variant="outline" className="text-red-500 hover:text-red-600" onClick={() => handleDelete(customer.id)} data-testid={`delete-customer-${customer.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {filtered.length === 0 && (
        <Card><CardContent className="py-12 text-center text-slate-500">
          {search ? "No customers match your search" : "No customers yet. Add your first customer!"}
        </CardContent></Card>
      )}

      {/* Add/Edit Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{selectedCustomer ? "Edit Customer" : "Add Customer"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <Label>Company Name *</Label>
                <Input value={form.company_name} onChange={e => setForm({ ...form, company_name: e.target.value })} required data-testid="input-company-name" />
              </div>
              <div>
                <Label>Contact Name *</Label>
                <Input value={form.contact_name} onChange={e => setForm({ ...form, contact_name: e.target.value })} required data-testid="input-contact-name" />
              </div>
            </div>
            <div>
              <Label>Address *</Label>
              <Textarea value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} required rows={2} data-testid="input-address" />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <Label>Phone *</Label>
                <Input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} required data-testid="input-phone" />
              </div>
              <div>
                <Label>Email</Label>
                <Input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} data-testid="input-email" />
              </div>
            </div>
            <div>
              <Label>GST Number</Label>
              <Input value={form.gst_number} onChange={e => setForm({ ...form, gst_number: e.target.value })} placeholder="e.g., 37AABCU9603R1ZM" data-testid="input-gst" />
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={2} data-testid="input-notes" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowModal(false)}>Cancel</Button>
              <Button type="submit" className="bg-forest hover:bg-forest/90" data-testid="submit-customer">{selectedCustomer ? "Update" : "Add"} Customer</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Customer Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Customer Details</DialogTitle>
          </DialogHeader>
          {customerDetail && (
            <div className="space-y-4">
              <div className="flex items-center gap-3 pb-4 border-b">
                <div className="w-12 h-12 bg-forest/10 rounded-lg flex items-center justify-center">
                  <Building2 className="w-6 h-6 text-forest" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg text-navy">{customerDetail.customer.company_name}</h3>
                  <p className="text-slate-500">{customerDetail.customer.contact_name}</p>
                </div>
              </div>

              <div className="grid gap-3 text-sm">
                {customerDetail.customer.address && (
                  <div className="flex gap-2"><MapPin className="w-4 h-4 text-slate-400 flex-shrink-0 mt-0.5" /><span>{customerDetail.customer.address}</span></div>
                )}
                {customerDetail.customer.phone && (
                  <div className="flex gap-2"><Phone className="w-4 h-4 text-slate-400" /><span>{customerDetail.customer.phone}</span></div>
                )}
                {customerDetail.customer.email && (
                  <div className="flex gap-2"><Mail className="w-4 h-4 text-slate-400" /><span>{customerDetail.customer.email}</span></div>
                )}
                {customerDetail.customer.gst_number && (
                  <div className="flex gap-2"><span className="text-slate-400">GST:</span><Badge variant="outline">{customerDetail.customer.gst_number}</Badge></div>
                )}
              </div>

              <div className="grid grid-cols-3 gap-4 pt-4 border-t">
                <div className="text-center p-3 bg-blue-50 rounded-lg">
                  <p className="text-2xl font-bold text-blue-600">₹{customerDetail.total_invoiced.toLocaleString()}</p>
                  <p className="text-xs text-slate-500">Total Invoiced</p>
                </div>
                <div className="text-center p-3 bg-green-50 rounded-lg">
                  <p className="text-2xl font-bold text-green-600">₹{customerDetail.total_paid.toLocaleString()}</p>
                  <p className="text-xs text-slate-500">Total Paid</p>
                </div>
                <div className="text-center p-3 bg-amber-50 rounded-lg">
                  <p className="text-2xl font-bold text-amber-600">₹{customerDetail.balance.toLocaleString()}</p>
                  <p className="text-xs text-slate-500">Balance Due</p>
                </div>
              </div>

              <div className="flex gap-4 text-sm text-slate-500 pt-2">
                <span>{customerDetail.invoices_count} Invoice(s)</span>
                <span>{customerDetail.payments_count} Payment(s)</span>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
